import React from 'react';
import { ListItem, ListItemText, Collapse, Typography, Icon, ListItemButton, ListItemIcon, Box } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { List } from '@mui/material';
import { MenuItem } from './menuConfig';
import { ShowForPermission } from './permissions/ShowForPermission';
import { ShowForRole } from './permissions/ShowForRole';

// Константы для анимации аккордеона
const ACCORDION_ANIMATION_DURATION = {
    enter: 500, // Время открытия в миллисекундах
    exit: 500,  // Время закрытия в миллисекундах
};

interface MenuItemComponentProps {
    item: MenuItem;
    currentIndex: string;
    accordionOpen: { [key: string]: boolean };
    closingIndex: string | null;
    onAccordionToggle: (index: string, isCurrentlyOpen: boolean) => void;
    onNavigate: (link: string) => void;
    onLogout: () => void;
    renderChildren: (items: MenuItem[], parentIndex: string) => React.ReactNode;
}

export const MenuItemComponent: React.FC<MenuItemComponentProps> = ({
    item,
    currentIndex,
    accordionOpen,
    closingIndex,
    onAccordionToggle,
    onNavigate,
    onLogout,
    renderChildren
}) => {
    const hasChildren = Boolean(item.children && item.children.length > 0);
    // Аккордеон открыт, если явно открыт через accordionOpen (для анимации) или если элемент активен
    // Приоритет ВСЕГДА отдаем accordionOpen для правильной работы анимации при ручном открытии
    // Если аккордеон закрывается, он не должен быть открыт
    const isAccordionOpen = closingIndex === currentIndex
        ? false // Если аккордеон закрывается, он закрыт
        : (accordionOpen.hasOwnProperty(currentIndex)
            ? accordionOpen[currentIndex]
            : Boolean(item.isActive));
    // Элемент выделяется, если он активен по пути, или если аккордеон открыт вручную
    const isHighlighted = (item.isActive && item.isPrimary) || 
                          (hasChildren && item.isPrimary && accordionOpen[currentIndex] && !item.isActive);
    const MyIcon = item.icon;

    const handleClick = () => {
        if (item.isDisabled) return;

        if (item.link === '/logout') {
            onLogout();
        } else if (hasChildren && item.isPrimary) {
            // Передаем текущее состояние аккордеона (учитываем и явное открытие, и активное состояние)
            const isCurrentlyOpen = accordionOpen.hasOwnProperty(currentIndex)
                ? accordionOpen[currentIndex]
                : (item.isActive || false);
            
            // Сначала переключаем аккордеон
            onAccordionToggle(currentIndex, isCurrentlyOpen);
            
            // Если аккордеон открывается и есть первый дочерний элемент с link, навигируем на него
            // Задержка должна учитывать время закрытия предыдущего аккордеона + время открытия нового
            if (!isCurrentlyOpen && item.children && item.children.length > 0) {
                const firstChildWithLink = item.children.find(child => child.link && !child.isDisabled);
                const linkToNavigate = firstChildWithLink?.link;
                if (linkToNavigate) {
                    // Навигация происходит после завершения анимации открытия
                    // При одновременном закрытии/открытии используем время открытия
                    setTimeout(() => {
                        onNavigate(linkToNavigate);
                    }, ACCORDION_ANIMATION_DURATION.enter);
                }
            }
        } else if (item.link) {
            onNavigate(item.link);
        }
    };

    const getListItemClassName = () => {
        if (isHighlighted) {
            return 'list-item';
        }
        return '';
    };

    const getButtonClassName = () => {
        if (!item.isPrimary) {
            return item.isActive ? 'list-item-child' : 'list-item-child-not-act';
        }
        return '';
    };

    const menuItemContent = (
        <>
            <ListItem
                button
                onClick={handleClick}
                disabled={item.isDisabled}
                className={getListItemClassName()}
                sx={{ 
                    marginTop: hasChildren || item.isPrimary ? 2 : 0,
                    transition: 'all 0.3s ease',
                    overflow: 'visible',
                    '& .MuiListItemButton-root': {
                        overflow: 'visible',
                    }
                }}
            >
                <ListItemButton 
                    className={getButtonClassName()}
                    sx={{ transition: 'all 0.3s ease' }}
                >
                    <ListItemIcon>
                        <Icon sx={{ ml: 0, mt: 0 }}>
                            {MyIcon ? <MyIcon stroke={isHighlighted ? "white" : null} /> : ''}
                        </Icon>
                    </ListItemIcon>
                    <ListItemText>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                ml: -4, 
                                mt: 0, 
                                fontWeight: item.isPrimary ? 'bold' : 'normal', 
                                fontSize: 15 
                            }}
                        >
                            &nbsp;{item.text}
                        </Typography>
                    </ListItemText>
                </ListItemButton>
                {hasChildren && item.isPrimary && (
                    <Box
                        sx={{
                            transition: `transform ${ACCORDION_ANIMATION_DURATION.enter}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                            transform: isAccordionOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {isAccordionOpen ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                )}
            </ListItem>
            {hasChildren && (
                <Collapse 
                    key={`collapse-${currentIndex}`}
                    in={isAccordionOpen} 
                    timeout={ACCORDION_ANIMATION_DURATION}
                    unmountOnExit={false}
                    sx={{
                        '& .MuiCollapse-wrapper': {
                            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                            overflow: 'hidden', // Скрываем overflow во время анимации
                        },
                        '& .MuiCollapse-wrapperInner': {
                            overflow: 'hidden', // Скрываем overflow во время анимации
                        },
                        '& .MuiCollapse-root': {
                            overflow: 'hidden', // Скрываем overflow во время анимации
                        },
                    }}
                >
                    <List 
                        component="div" 
                        disablePadding
                        sx={{
                            overflow: 'hidden', // Скрываем overflow, чтобы предотвратить появление полос прокрутки
                        }}
                    >
                        {renderChildren(item.children ?? [], `${currentIndex}-`)}
                    </List>
                </Collapse>
            )}
        </>
    );

    // Оборачиваем в компоненты проверки прав доступа при необходимости
    if (item.requiredRole) {
        return (
            <ShowForRole key={currentIndex} role={item.requiredRole}>
                {menuItemContent}
            </ShowForRole>
        );
    }

    if (item.requiredPermission) {
        return (
            <ShowForPermission
                key={currentIndex}
                catalog={item.requiredPermission.catalog}
                permission={item.requiredPermission.permission}
            >
                {menuItemContent}
            </ShowForPermission>
        );
    }

    return <React.Fragment key={currentIndex}>{menuItemContent}</React.Fragment>;
};

